import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AngleRight, EllipsisH } from '@mehilainen/mds-icons';
import { Gray100, Gray200, Gray300, Gray600, Gray700, Gray900, Primary500, } from '@mehilainen/mds-tokens/colors';
import { spacing } from '@mehilainen/mds-tokens/spacing';
import { baseTypography } from '@mehilainen/mds-tokens/typography';
import { Box, styled, Typography, } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { shouldForwardProp } from '../mds-shared';
const titleSizeMap = {
    medium: {
        fontSize: baseTypography.size[400].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
    small: {
        fontSize: baseTypography.size[300].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
};
const descriptionSizeMap = {
    medium: {
        fontSize: baseTypography.size[300].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
    small: {
        fontSize: baseTypography.size[200].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
};
const InnerContainer = styled(Box)(() => ({
    display: 'flex',
    padding: `0 ${spacing[500].px}`,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '& > *:first-of-type': {
        marginRight: spacing[500].px,
    },
    '& > *:not(:first-of-type)': {
        marginRight: 0,
    },
    // Counter default styles
    '&[href], &[role="button"]': {
        textDecoration: 'none',
        color: 'inherit',
        background: 'none',
        border: 'none',
        font: 'inherit',
        textAlign: 'inherit',
        padding: 0,
        margin: 0,
        width: '100%',
    },
    '&:hover': {
        backgroundColor: Gray100,
    },
    '&:active': {
        backgroundColor: Gray200,
    },
    '&:focus': {
        backgroundColor: 'initial',
    },
}));
const OuterContainer = styled(Box)(() => ({
    listStyleType: 'none',
}));
const ContentContainer = styled(Box, { shouldForwardProp })(() => ({
    display: 'flex',
    width: '90%',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${Gray300}`,
}));
const TextContainer = styled(Box, { shouldForwardProp })(() => ({
    display: 'flex',
    padding: `${spacing[550].px} ${spacing[500].px} ${spacing[550].px} 0`,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: spacing[200].px,
    flex: '1 0 0',
}));
const Title = styled(Typography, {
    shouldForwardProp: (prop) => prop !== '$size',
})(({ $size }) => (Object.assign({}, titleSizeMap[$size])));
const Description = styled(Typography, {
    shouldForwardProp: (prop) => prop !== '$size',
})(({ $size }) => (Object.assign({}, descriptionSizeMap[$size])));
/**
 * ListBlock component for showing list items (blocks)
 *
 * Usage:
 *   <ListBlock
 *     title="Item Title"
 *     description="Optional description of the item"
 *     startIcon={<CustomIcon />}
 *     endIcon={<AngleRight />}
 *     size="medium"
 *     onClick={() => handleClick()}
 *     href="https://example.com"
 *     target="_blank"
 *     sx={{ custom: 'styles' }}
 *   />
 *
 * Props:
 * - title: string - The main text of the list item
 * - description?: string - Optional secondary text
 * - startIcon?: React.ReactNode | false - Icon to display at the start of the item. Defaults to EllipsisH. Use false to remove the icon.
 * - endIcon?: React.ReactNode | false - Icon to display at the end of the item. Defaults to AngleRight. Use false to remove the icon.
 * - size?: 'medium' | 'small' - Controls the overall size of the component, affecting text size and component dimensions (defaults to 'medium')
 * - onClick?: function - Handler for click events on the list item. If provided (and href is not), the component renders as a button.
 * - sx?: SxProps - Custom styles using MUI's sx prop
 * - href?: string - URL to navigate to when the list item is clicked. If provided, the component renders as an anchor tag.
 * - target?: string - Specifies where to open the linked document (e.g., "_blank" for a new tab). Only applicable when href is provided.
 * - TitleProps?: TypographyProps - Additional props for the title Typography component
 * - DescriptionProps?: TypographyProps - Additional props for the description Typography component
 */
let idCounter = 0;
// TODO: Replace with React.useId() when it is available
function generateUniqueId(prefix) {
    idCounter += 1;
    return `${prefix}-${idCounter}`;
}
const defaultProps = {
    endIcon: (_jsx(AngleRight, { fontSize: "large", sx: { fill: Gray700 } })),
    size: 'medium',
    startIcon: (_jsx(EllipsisH, { sx: { fill: Primary500 } })),
};
export const ListBlock = ({ DescriptionProps, TitleProps, description, endIcon = defaultProps.endIcon, href, onClick, size = defaultProps.size, startIcon = defaultProps.startIcon, sx, target, title, }) => {
    const ElementType = href ? 'a' : 'button';
    const labelId = title ? generateUniqueId('list-block-label') : undefined;
    const descriptionId = description
        ? generateUniqueId('list-block-desc')
        : undefined;
    if (href && onClick) {
        // eslint-disable-next-line no-console
        console.warn('Both href and onClick provided to ListBlock. href will take precedence.');
    }
    return (_jsx(OuterContainer, Object.assign({ as: "li" }, { children: _jsxs(InnerContainer, Object.assign({ as: ElementType, 
            // @ts-expect-error: href is not a valid prop for button
            href: href, target: href ? target : undefined, onClick: href ? undefined : onClick, role: href ? undefined : 'button', tabIndex: 0, "aria-labelledby": labelId, "aria-describedby": descriptionId, sx: sx }, { children: [startIcon && _jsx(Box, Object.assign({ "aria-hidden": "true" }, { children: startIcon })), _jsxs(ContentContainer, Object.assign({ "aria-hidden": "true" }, { children: [_jsxs(TextContainer, { children: [title && (_jsx(Title, Object.assign({ "$size": size, color: Gray900 }, TitleProps, { children: title }))), description && (_jsx(Description, Object.assign({ "$size": size, color: Gray600 }, DescriptionProps, { children: description })))] }), endIcon && _jsx(Box, { children: endIcon })] })), title && (_jsx(Box, Object.assign({ id: labelId, sx: visuallyHidden }, { children: title }))), description && (_jsx(Box, Object.assign({ id: descriptionId, sx: visuallyHidden }, { children: description })))] })) })));
};
